import { type ReactNode, useEffect, useRef, useState } from 'react';

// ONE CELL OF THE INFO TABLE, EDITED WHERE IT IS: the value until clicked, a field while you
// are in it. Enter or leaving the field saves, Escape puts it back. No pencil and no modal:
// every field is edited the same way, so nothing has to say which ones can be.
//
// `value` is what is stored and what the field starts from; `children` is how it reads at
// rest (a formatted time, a localized category), which can differ from it. `options` makes
// the field a dropdown of those values.
export function InfoEditCell({
  value,
  onSave,
  children,
  options,
  title,
}: {
  value: string;
  onSave: (value: string) => void;
  children: ReactNode;
  options?: { value: string; label: string }[];
  title: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const ref = useRef<HTMLInputElement & HTMLSelectElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);
  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);

  const commit = (v: string) => {
    setEditing(false);
    if (v.trim() !== value.trim()) onSave(v.trim());
  };
  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const field = 'w-full rounded-md border border-blue-400 bg-white px-1.5 py-1 text-center text-sm font-semibold text-slate-900 outline-none';

  if (editing && options) {
    return (
      <select
        ref={ref}
        value={draft}
        onChange={(e) => commit(e.target.value)}
        onBlur={() => setEditing(false)}
        onKeyDown={(e) => e.key === 'Escape' && cancel()}
        className={field}
      >
        <option value="" />
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (editing) {
    return (
      <input
        ref={ref}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => commit(draft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit(draft);
          } else if (e.key === 'Escape') {
            cancel();
          }
        }}
        className={field}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      title={title}
      className="block min-h-[1.5rem] w-full rounded-md transition-colors hover:bg-blue-50"
    >
      {children}
    </button>
  );
}
